import React, { ReactNode, useEffect, useState } from 'react';
import Button from '../../common/Button';
import { MemberDTO } from '../../../services/apiClient';
import FormField from '../../common/FormField';
import handleChange from '../../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../../hooks/useFormValidation';
import { useFileApi } from '../../../hooks/useFileApi';
import { useMemberIdTypes } from '../../../hooks/useMemberIdTypeApi';
import { useCountries } from '../../../hooks/useCountryApi';
import { useLanguages } from '../../../hooks/useLanguageApi';
import { useMemberTitles } from '../../../hooks/useMemberTitleApi';
import { useGenders } from '../../../hooks/useGenderApi';
import { MemberIdTypeConstants } from '../../../constants/MemberIdType';
import { useMemberApi } from '../../../hooks/useMemberApi';
import { CountryConstant } from '../../../constants/CountryConstants';
import FileUpload from '../../common/FileUpload';
import { FileTypeConstants } from '../../../constants/FileTypeConstants';

interface Props {
  member: MemberDTO;
  onChange: (updated: Partial<MemberDTO>) => void;
  onNext: () => void;
}

const PersonalInfo: React.FC<Props> = ({ member, onChange, onNext }) => {
  const [formData, setFormData] = useState<MemberDTO>(member);
  const [isSouthAfrican, setIsSouthAfrican] = useState(true);
  const [languageNotOnList, setLanguageNotOnList] = useState(false);
  const { checkIdExists, isLoading } = useMemberApi();
  const [idExistsError, setIdExistsError] = useState<string>('');

  const { uploadFile } = useFileApi();

  const [uploadingFiles, setUploadingFiles] = useState<{
    [key: string]: boolean;
  }>({});

  // Define the type for dropdown options
  type DropdownOption = { value: string; label: ReactNode }; // Updated label type to ReactNode




  const validationRules: ValidationRules = {
    firstName: [{ required: true, message: 'Name is required' }],
    lastName: [{ required: true, message: 'Last Name is required' }],
    initials: [{ required: true, message: 'Initials is required' }],
    memberTitleId: [{ required: true, message: 'Title is required' }],
    memberIdTypeId: [{ required: true, message: 'Citizenship is required' }],
    idPassportNumber: [
      {
        required: true,
        message: isSouthAfrican
          ? 'SA ID Number is required'
          : 'Passport Number is required',
      },
      {
        custom: (value) => {
          if (!value) return true; // let "required" handle empties
          if (!isSouthAfrican) return true; // skip passport validation
          return validateSAIdNumber(String(value));
        },
        message: 'SA ID Number is not valid', // passport isn’t validated, so no need for a passport message
      },
    ],
    dateOfBirth: [
      { required: !isSouthAfrican, message: 'Date Of Birth is required' },
    ],
    countryId: [{ required: !isSouthAfrican, message: 'Country is required' }],
    genderId: [{ required: !isSouthAfrican, message: 'Gender is required' }],
    languageId: [{ required: true, message: 'First Language is required' }],
    languageOther: [
      { required: languageNotOnList, message: 'Other Language is required' },
    ],
    proofOfIdFileId: [{ required: true, message: 'Proof Of Id File is required' }],
  };
  const { errors, validate } = useFormValidation<MemberDTO>(validationRules);

  //CALL USE API HOOK => HERE CHECK IF EXISTS
  const validateSAIdNumber = (idNumber: string): boolean => {
    // Must be exactly 13 digits
    if (!/^\d{13}$/.test(idNumber)) {
      return false;
    }

    // Validate date (YYMMDD)
    const year = parseInt(idNumber.substring(0, 2), 10);
    const month = parseInt(idNumber.substring(2, 4), 10);
    const day = parseInt(idNumber.substring(4, 6), 10);

    const fullYear =
      year >= 0 && year <= new Date().getFullYear() % 100
        ? 2000 + year
        : 1900 + year;

    const date = new Date(fullYear, month - 1, day);
    if (
      date.getFullYear() !== fullYear ||
      date.getMonth() + 1 !== month ||
      date.getDate() !== day
    ) {
      return false; // Invalid date
    }

    // Luhn algorithm for checksum
    let sum = 0;
    let alternate = false;
    for (let i = idNumber.length - 1; i >= 0; i--) {
      let n = parseInt(idNumber.charAt(i), 10);
      if (alternate) {
        n *= 2;
        if (n > 9) n -= 9;
      }
      sum += n;
      alternate = !alternate;
    }
    return sum % 10 === 0;
  };

  const handleIdExists = async (id: string | undefined) => {
    if (id) {
      try {
        const idExistsResponse = await checkIdExists(id);
        if (idExistsResponse?.data === true) {
          setIdExistsError('Id Number already exists in the system');
          return true;
        } else {
          setIdExistsError('');
          return false;
        }
      } catch (error) {
        console.error('Error checking ID existence:', error);
        return true;
      }
    }
  };

  const handleNext = async () => {
    const idExists = await handleIdExists(formData.idPassportNumber);
    if (validate(formData) && !idExists) {
      onNext();
    }
  };

  const { data: countries, isLoading: isLoadingCountries } = useCountries();
  const { data: languages, isLoading: isLoadingLanguages } = useLanguages();
  const { data: titlesData, isLoading: isLoadingTitles } = useMemberTitles();
  const { data: idTypesData, isLoading: isLoadingIdTypes } = useMemberIdTypes();
  const { data: gendersData, isLoading: isLoadingGenders } = useGenders();

  useEffect(() => {
    if (member.memberIdTypeId != undefined) {
      if (
        member.memberIdTypeId ===
        MemberIdTypeConstants.SOUTH_AFRICAN_CITIZEN.toLowerCase()
      ) {
        const updatedMember = {
          ...formData,
          countryId: CountryConstant.SOUTH_AFRICA,
        };
        setFormData(updatedMember);
        onChange({ countryId: CountryConstant.SOUTH_AFRICA });
      }
    }
  }, [member.memberIdTypeId]);

  useEffect(() => {
    if (
      isSouthAfrican &&
      formData.idPassportNumber &&
      formData.idPassportNumber.length === 13
    ) {
      //Setting Gender from Id number
      const genderDigit = parseInt(formData.idPassportNumber.charAt(6), 10);
      const isMale = genderDigit >= 5;

      const maleGenderOption = gendersData?.find(
        (g) =>
          g.title?.toLowerCase().includes('male') &&
          !g.title.toLowerCase().includes('female'),
      );

      const femaleGenderOption = gendersData?.find((g) =>
        g.title?.toLowerCase().includes('female'),
      );

      if (isMale && maleGenderOption) {
        const updatedMember = { ...formData, genderId: maleGenderOption.id ?? '' };
        setFormData(updatedMember);
        onChange({ genderId: maleGenderOption.id ?? '' });
      } else if (!isMale && femaleGenderOption) {
        const updatedMember = {
          ...formData,
          genderId: femaleGenderOption.id ?? '',
        };
        setFormData(updatedMember);
        onChange({ genderId: femaleGenderOption.id ?? '' });
      }

      //Setting DOB from Id number
      const year = parseInt(formData.idPassportNumber.substring(0, 2), 10);
      const month = parseInt(formData.idPassportNumber.substring(2, 4), 10);
      const day = parseInt(formData.idPassportNumber.substring(4, 6), 10);
      const fullYear =
        year >= 0 && year <= new Date().getFullYear() % 100
          ? 2000 + year
          : 1900 + year;

      // Use Date.UTC to avoid timezone conversion issues
      const dateOfBirth = new Date(Date.UTC(fullYear, month - 1, day));

      const dateObj = new Date(fullYear, month - 1, day);
      if (
        dateObj.getFullYear() === fullYear &&
        dateObj.getMonth() + 1 === month &&
        dateObj.getDate() === day
      ) {
        const updatedMemberWithDate: MemberDTO = { ...formData, dateOfBirth };
        setFormData(updatedMemberWithDate);
        onChange({ dateOfBirth });
      }
    }
  }, [formData.idPassportNumber, isSouthAfrican, gendersData]);

  useEffect(() => {
    if (formData.memberIdTypeId === MemberIdTypeConstants.SOUTH_AFRICAN_CITIZEN)
      setIsSouthAfrican(true);
    else if (formData.memberIdTypeId === MemberIdTypeConstants.OTHER)
      setIsSouthAfrican(false);
  }, [formData.memberIdTypeId]);

  const handleFileChange = (field: keyof MemberDTO) => async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        setUploadingFiles((prev) => ({ ...prev, [field]: true }));
        const response = await uploadFile('files', file.name, file, 'members');
        if (response && response.success && response.data) {
          setFormData((prev) => ({
            ...prev,
            [field]: response.data, // Save the file ID
          }));
          onChange({ [field]: response.data } as Partial<MemberDTO>);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
      } finally {
        setUploadingFiles((prev) => ({ ...prev, [field]: false }));
      }
    }
  };

  return (
    <div>
      <FormField
        id="memberTitleId"
        label="Title"
        type="dropdown"
        value={formData.memberTitleId || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Select Title"
        required={true}
        error={errors.memberTitleId}
        options={(titlesData || []).map((t) => ({
          value: t.id ?? '',
          label: t.title ?? '',
        }))}
        isLoading={isLoadingTitles}
      />
      <FormField
        id="initials"
        label="Initials"
        type="text"
        value={formData.initials || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Enter Initials"
        required={true}
        error={errors.initials}
        textCase="uppercase"
      />
      <FormField
        id="firstName"
        label="Name"
        type="text"
        value={formData.firstName || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Enter First Name"
        required={true}
        error={errors.firstName}
        textCase="titlecase"
      />

      <FormField
        id="lastName"
        label="Surname"
        type="text"
        value={formData.lastName || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Enter Last Name"
        required={true}
        error={errors.lastName}
        textCase="titlecase"
      />

      <FormField
        id="memberIdTypeId"
        label="Citizenship?"
        type="dropdown"
        value={formData.memberIdTypeId || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Select Citizenship"
        required={true}
        error={errors.memberIdTypeId}
        options={(idTypesData || []).map((it) => ({
          value: it.id ?? '',
          label: it.title ?? '',
        }))}
        isLoading={isLoadingIdTypes}
      />

      <FormField
        id="idPassportNumber"
        label={isSouthAfrican ? 'SA ID Number' : 'Passport Number'}
        type="text"
        value={formData.idPassportNumber || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder={isSouthAfrican ? 'e.g 8101015025086' : 'e.g AB56796-92'}
        required={true}
        error={errors.idPassportNumber || idExistsError}
      />

      {!isSouthAfrican && (
        <>
          <FormField
            id="dateOfBirth"
            label="Date Of Birth"
            type="date"
            value={formData.dateOfBirth || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            placeholder="Enter Date Of Birth"
            required={!isSouthAfrican}
            error={errors.dateOfBirth}
          />
          <FormField
            id="countryId"
            label="Country"
            type="dropdown"
            value={formData.countryId || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            placeholder="Select Country"
            required={!isSouthAfrican}
            error={errors.countryId}
            options={(countries || []).map((c) => ({ value: c.id ?? '', label: c.title ?? '' }))}
            isLoading={isLoadingCountries}
          />
          <FormField
            id="genderId"
            label="Gender"
            type="dropdown"
            value={formData.genderId || ''}
            onChange={(e) => handleChange(e, setFormData, onChange)}
            placeholder="Select Gender"
            required={!isSouthAfrican}
            error={errors.genderId}
            options={(gendersData || []).map((g) => ({ value: g.id ?? '', label: g.title ?? '' }))}
            isLoading={isLoadingGenders}
          />
        </>
      )}

      <FileUpload
        id="proofOfIdFileId"
        onChange={handleFileChange('proofOfIdFileId')}
        title="Upload Proof Of Id File"
        accept={FileTypeConstants.ALL_SUPPORTED}
        required={true}
        error={errors.proofOfIdFileId}
        isUploading={uploadingFiles['proofOfIdFileId']}
      />

      <FormField
        id="languageId"
        label="First Language"
        type="dropdown"
        value={formData.languageId || ''}
        onChange={(e) => handleChange(e, setFormData, onChange)}
        placeholder="Select Language"
        required={true}
        error={errors.languageId}
        options={(languages || []).map((l) => ({
          value: l.id ?? '',
          label: l.title ?? '',
        }))}
        isLoading={isLoadingLanguages}
      />

      {languageNotOnList && (
        <FormField
          id="languageOther"
          label="Other Language"
          type="text"
          value={formData.languageOther || ''}
          onChange={(e) => handleChange(e, setFormData, onChange)}
          placeholder="e.g Portuguese"
          required={languageNotOnList}
          error={errors.languageOther}
        />
      )}

      <div className="mt-4 flex justify-end">
        <Button onClick={handleNext} variant="primary">
          Next
        </Button>
      </div>
    </div>
  );
};

export default PersonalInfo;
